import axios from 'axios';
import { formatErrorForUser, isNetworkError, getNetworkErrorMessage } from '../utils/errorUtils';
import requestQueueManager from '../utils/requestQueueManager';
import { validateUserHasHotel } from '../utils/userValidation';

// Reports scan whole date ranges and exports build files: they get a longer
// budget than the request queue's 10s default.
const REPORT_ENDPOINT = /\/reports?\/|\/export\b/;
const REPORT_TIMEOUT_MS = 60000;

/**
 * Base API client for handling HTTP requests to the hotel management system API using Axios
 */
class ApiClient {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    this.tokenKey = 'authToken';
    this.refreshTokenKey = 'refreshToken';
    this.tokenExpiryKey = 'tokenExpiry';
    this.currentUserKey = 'currentUser';
    
    // Initialize tokens from localStorage, surviving corrupt values. A
    // client that treated the OTP challenge (202, no tokens) as a normal
    // login once stored the literal string "undefined" in these keys, and
    // JSON.parse of that crashed the whole app at boot (2026-10-24).
    // Anything unreadable here means "logged out", never a crash.
    const readStored = (key) => {
      const raw = localStorage.getItem(key);
      return !raw || raw === 'undefined' || raw === 'null' ? null : raw;
    };
    this.token = readStored(this.tokenKey);
    this.refreshToken = readStored(this.refreshTokenKey);
    this.tokenExpiry = readStored(this.tokenExpiryKey);
    try {
      const rawUser = readStored(this.currentUserKey);
      this.currentUser = rawUser ? JSON.parse(rawUser) : null;
    } catch (e) {
      console.error('Discarding unreadable stored session:', e);
      this.currentUser = null;
    }
    // Heal poisoned storage: without a usable token the other keys are
    // dead weight that only confuse isAuthenticated and the interceptors.
    if (!this.token || !this.currentUser) {
      this.token = null;
      this.refreshToken = null;
      this.tokenExpiry = null;
      this.currentUser = null;
      try {
        [this.tokenKey, this.refreshTokenKey, this.tokenExpiryKey, this.currentUserKey]
          .forEach((key) => localStorage.removeItem(key));
      } catch (e) { /* storage unavailable — in-memory nulls are enough */ }
    }
    
    // Create an axios instance with default configuration
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    // Set up request interceptor to add auth token to all requests
    this.api.interceptors.request.use(
      async (config) => {
        // Check token expiry and refresh if needed before making the request
        if (this.token && this.isTokenExpiring() && this.refreshToken) {
          try {
            await this.refreshAccessToken();
          } catch (error) {
            // If refresh fails, log out user
            console.error('Token refresh failed:', error);
            this.logout();
            // Redirect to login will happen based on 401 response 
          }
        }
        
        // Add token to request headers if available
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    
    // Wire the assist-settled interceptor so waitFor kind:api_settled works.
    // Lazy import avoids a circular dependency (ApiClient ← assistExecutor
    // ← assistantScreenContext — all read-only from ApiClient's perspective).
    import('../utils/assistExecutor').then(({ installApiInterceptor }) => {
      installApiInterceptor(this.api);
    }).catch(() => {});

    // Set up response interceptor to handle errors and extract data
    this.api.interceptors.response.use(
      (response) => {
        return response.data;
      },
      (error) => {
        // Handle network errors
        if (isNetworkError(error)) {
          const networkError = {
            message: getNetworkErrorMessage(error),
            code: 'NETWORK_ERROR',
            status: 0,
            isNetworkError: true
          };
          return Promise.reject(networkError);
        }

        // Handle unauthorized errors (expired token)
        if (error.response && error.response.status === 401) {
          // If we're not trying to refresh or login
          if (!error.config.url.includes('auth/refresh') && 
              !error.config.url.includes('auth/token')) {
            this.logout();
            // Redirect to login page
            window.location.href = '/login';
          }
        }
        
        // Format error object consistently
        const errorData = error.response?.data?.error || {
          message: error.response?.data?.detail || error.message,
          code: 'UNKNOWN_ERROR'
        };
        
        const formattedError = {
          status: error.response?.status || 500,
          detail: error.response?.data?.detail, // Preserve the detail field
          data: error.response?.data, // Preserve full response data for field-level errors
          ...errorData
        };
        
        return Promise.reject(formattedError);
      }
    );

    // Initialize user context if already authenticated
    this.initializeUserContext();
  }

/**
   * Login user and get tokens
   * @param {string} username - User username
   * @param {string} password - User password
   * @param {string} [captchaToken] - Optional Cloudflare Turnstile token
   * @returns {Promise} Promise resolving to user data
   */
async login(username, password, captchaToken, otp) {
  try {
      // Use axios directly for login to avoid interceptors
      const payload = { username, password };
      if (captchaToken) payload.cf_turnstile_token = captchaToken;
      // Elevated roles (manager and up) need an emailed one-time code once
      // every 24h. A previous verification left a signed trust token; send
      // it so the server can skip the code inside that window.
      try {
        const trust = localStorage.getItem('fd_otp_trust');
        if (trust) payload.otp_trust = trust;
      } catch (e) { /* storage unavailable — the server will just challenge */ }
      if (otp && otp.code) {
        payload.otp_code = otp.code;
        payload.otp_challenge = otp.challenge;
      }
      const response = await axios.post(`${this.baseURL}/auth/token/`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      // 202: the password was right but a code was emailed — nothing is
      // stored, the caller shows the code form and calls login() again.
      if (response.status === 202 || response.data?.otp_required) {
        return {
          otpRequired: true,
          challenge: response.data.otp_challenge,
          emailHint: response.data.email_hint || '',
        };
      }

      // Extract tokens and expiry from response
      const { access, refresh, user, otp_trust } = response.data;
      if (!access || !refresh || !user) {
        // Never let a shape change write garbage into storage again.
        throw new Error('Unexpected sign-in response. Please try again.');
      }
      if (otp_trust) {
        try { localStorage.setItem('fd_otp_trust', otp_trust); } catch (e) { /* per-device nicety only */ }
      }
      
      // Parse token to get expiry
      const tokenExpiry = this.getTokenExpiry(access);
      
      // Store tokens and expiry
      this.setTokens(access, refresh, tokenExpiry);
      
      // Set current user data
      this.setCurrentUser(user);
      
      return user;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/**
 * Superadmin only: every hotel, newest first, with the lead-spotting
 * fields (trial flag, activity recency, onboarding, rooms, users).
 */
async superadminHotels() {
  return this.get('/auth/superadmin/hotels/');
}

/**
 * Superadmin only: enter a hotel as its admin on a brand-new session.
 * The old refresh token is sent for server-side revocation, then BOTH
 * web storages are wiped before the new pair is stored — nothing cached
 * from the previous property survives the switch. Caller reloads.
 */
async superadminSwitchHotel(hotelId) {
  const body = await this.post('/auth/superadmin/switch-hotel/', {
    hotel_id: hotelId,
    refresh: this.refreshToken,
  });
  const { access, refresh, user } = body || {};
  if (!access || !refresh || !user) {
    throw new Error('Unexpected switch response. Please try again.');
  }
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (e) { /* storage unavailable — in-memory state is replaced below */ }
  this.setTokens(access, refresh, this.getTokenExpiry(access));
  this.setCurrentUser(user);
  return user;
}

/**
 * Redeem a one-click magic-login token (unauthenticated) from the trial
 * signup email and establish a session — identical storage mechanism to
 * login() (same setTokens/setCurrentUser calls), since the endpoint
 * returns the same JWT payload shape as CustomTokenObtainPairView.
 *
 * Never logs the token. Uses axios directly (like login()) to avoid the
 * request interceptor attaching a stale Authorization header, and to
 * avoid the response interceptor's 401 auto-logout/redirect kicking in
 * on a failed one-time-use token.
 *
 * @param {string} token - single-use magic-login token from the emailed link
 * @returns {Promise} Promise resolving to user data
 */
async magicLogin(token) {
  try {
    const response = await axios.post(`${this.baseURL}/auth/magic-login/`, {
      token
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    // Extract tokens and expiry from response (same shape as login())
    const { access, refresh, user } = response.data;

    // Parse token to get expiry
    const tokenExpiry = this.getTokenExpiry(access);

    // Store tokens and expiry — identical mechanism/keys to login()
    this.setTokens(access, refresh, tokenExpiry);

    // Set current user data
    this.setCurrentUser(user);

    return user;
  } catch (error) {
    // Do not log the token itself — only the error object, which never
    // contains the request payload.
    console.error('Magic login error:', error);
    throw error;
  }
}

/**
 * Refresh access token using refresh token
 * @returns {Promise} Promise resolving to new access token
 */
async refreshAccessToken() {
  if (!this.refreshToken) {
    console.warn('No refresh token available for refresh');
    throw new Error('No refresh token available');
  }
  
  // Prevent multiple simultaneous refresh attempts
  if (this.refreshPromise) {
    return this.refreshPromise;
  }
  
  this.refreshPromise = (async () => {
    try {
      // Use axios directly for refresh to avoid interceptors
      const response = await axios.post(`${this.baseURL}/auth/token/refresh/`, {
        refresh: this.refreshToken
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 10000 // 10 second timeout for token refresh
      });
      
      const { access } = response.data;
      const tokenExpiry = this.getTokenExpiry(access);
      
      // Update just the access token, keep the same refresh token
      this.setTokens(access, this.refreshToken, tokenExpiry);
      
      return access;
    } catch (error) {
      console.error('Token refresh failed:', error.response?.data || error.message);
      
      // Only logout if the refresh token is truly invalid (401)
      if (error.response?.status === 401) {
        this.logout();
      }
      throw error;
    } finally {
      this.refreshPromise = null;
    }
  })();
  
  return this.refreshPromise;
}

/**
 * Parse JWT token to get expiry timestamp
 * @param {string} token - JWT token
 * @returns {number} Timestamp of token expiry
 */
getTokenExpiry(token) {
  try {
    // Get the payload part of the JWT
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const { exp } = JSON.parse(jsonPayload);
    return exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Error parsing token:', error);
    return 0;
  }
}

/**
 * Check if token is expiring in less than 5 minutes
 * @returns {boolean} True if token is expiring soon
 */
isTokenExpiring() {
  if (!this.tokenExpiry) return true;
  
  // Check if token expires in less than 5 minutes
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000;
  
  return now + fiveMinutes > this.tokenExpiry;
}

/**
 * Set authentication tokens in storage
 * @param {string} token - Access token
 * @param {string} refreshToken - Refresh token
 * @param {number} expiry - Token expiry timestamp
 */
setTokens(token, refreshToken, expiry) {
  if (!token || !refreshToken) {
    // Storing a missing token writes the string "undefined", which a past
    // bug then fed to JSON.parse at boot. Refuse instead.
    console.error('setTokens called without tokens — ignoring');
    return;
  }
  this.token = token;
  this.refreshToken = refreshToken;
  this.tokenExpiry = expiry;

  localStorage.setItem(this.tokenKey, token);
  localStorage.setItem(this.refreshTokenKey, refreshToken);
  localStorage.setItem(this.tokenExpiryKey, expiry == null ? '' : expiry);
}

/**
 * Set current user data in storage
 * @param {Object} user - User data with hotel information
 */
setCurrentUser(user) {
  if (!user) {
    console.error('setCurrentUser called without a user — ignoring');
    return;
  }
  this.currentUser = user;
  localStorage.setItem(this.currentUserKey, JSON.stringify(user));
}

/**
 * Get hotel context headers
 * @returns {Object} Headers object with hotel context
 */
getHotelContextHeaders() {
  const headers = {};
  
  if (this.currentUser?.user_profile?.hotel?.id) {
    headers['X-Hotel-Context'] = this.currentUser.user_profile.hotel.id.toString();
  } else if (this.currentUser?.hotel?.id) {
    headers['X-Hotel-Context'] = this.currentUser.hotel.id.toString();
  } else {
  }
  
  return headers;
}

/**
 * Clear authentication tokens
 */
logout() {
  this.token = null;
  this.refreshToken = null;
  this.tokenExpiry = null;
  this.currentUser = null;
  
  localStorage.removeItem(this.tokenKey);
  localStorage.removeItem(this.refreshTokenKey);
  localStorage.removeItem(this.tokenExpiryKey);
  localStorage.removeItem(this.currentUserKey);
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated
 */
isAuthenticated() {
  return Boolean(this.token && !this.isTokenExpiring());
}


  /**
   * Perform GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise} Promise resolving to response data
   */
  async get(endpoint, paramsOrConfig = {}) {
    const hotelHeaders = this.getHotelContextHeaders();
    
    // Handle both old signature (params) and new signature (config object with params, responseType, etc.)
    let config = {};
    if (paramsOrConfig.responseType || paramsOrConfig.headers || paramsOrConfig.timeout) {
      // New signature: full config object
      config = {
        ...paramsOrConfig,
        headers: {
          ...hotelHeaders,
          ...paramsOrConfig.headers
        }
      };
    } else {
      // Old signature: just params
      config = {
        params: paramsOrConfig,
        headers: {
          ...hotelHeaders
        }
      };
    }
    
    // Use request queue manager for non-critical requests
    const priority = this._getRequestPriority(endpoint, 'GET');
    
    // Bypass queue for critical authentication requests
    if (priority === 'bypass') {
      return await this.api.get(endpoint, config);
    }
    
    // The queue cuts requests off at 10s by default. Reports scan whole
    // date ranges (and exports build files), so they get a longer budget;
    // an explicit `timeout` in the config always wins.
    const timeout = config.timeout || (REPORT_ENDPOINT.test(endpoint) ? REPORT_TIMEOUT_MS : undefined);

    return await requestQueueManager.enqueue(
      () => this.api.get(endpoint, config),
      { priority, ...(timeout ? { timeout } : {}) }
    );
  }

  /**
   * Perform POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise} Promise resolving to response data
   */
  async post(endpoint, data = {}, options = {}) {
    const hotelHeaders = this.getHotelContextHeaders();

    // Use request queue manager with higher priority for write operations
    const priority = options.priority || this._getRequestPriority(endpoint, 'POST');

    const axiosConfig = {
      headers: {
        ...hotelHeaders
      },
      // Long-running endpoints (assistant chat = sequential LLM tool
      // rounds, 5-20s+) must outlive the queue's 10s default, and the
      // axios socket should be capped at the same budget.
      ...(options.timeout ? { timeout: options.timeout } : {})
    };

    // Bypass queue for critical authentication requests
    if (priority === 'bypass') {
      return await this.api.post(endpoint, data, axiosConfig);
    }

    return await requestQueueManager.enqueue(
      () => this.api.post(endpoint, data, axiosConfig),
      {
        priority,
        ...(options.timeout ? { timeout: options.timeout } : {}),
        // retries: 0 matters for non-idempotent calls (an LLM turn that
        // "timed out" client-side still completes and bills server-side —
        // a retry double-executes it).
        ...(options.retries !== undefined ? { retries: options.retries } : {})
      }
    );
  }

  /**
   * Perform PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise} Promise resolving to response data
   */
  async put(endpoint, data = {}, options = {}) {
    const hotelHeaders = this.getHotelContextHeaders();

    // Use request queue manager with higher priority for write operations
    const priority = this._getRequestPriority(endpoint, 'PUT');

    return await requestQueueManager.enqueue(
      () => this.api.put(endpoint, data, {
        headers: {
          ...hotelHeaders
        },
        ...(options.timeout ? { timeout: options.timeout } : {})
      }),
      {
        priority,
        ...(options.timeout ? { timeout: options.timeout } : {}),
        ...(options.retries !== undefined ? { retries: options.retries } : {})
      }
    );
  }

  /**
   * Perform DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise} Promise resolving to response data
   */
  async delete(endpoint) {
    const hotelHeaders = this.getHotelContextHeaders();
    
    // Use request queue manager with higher priority for write operations
    const priority = this._getRequestPriority(endpoint, 'DELETE');
    
    return await requestQueueManager.enqueue(
      () => this.api.delete(endpoint, {
        headers: {
          ...hotelHeaders
        }
      }),
      { priority }
    );
  }
  
  /**
   * Perform PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise} Promise resolving to response data
   */
  async patch(endpoint, data = {}) {
    const hotelHeaders = this.getHotelContextHeaders();
    
    // Use request queue manager with higher priority for write operations
    const priority = this._getRequestPriority(endpoint, 'PATCH');
    
    return await requestQueueManager.enqueue(
      () => this.api.patch(endpoint, data, {
        headers: {
          ...hotelHeaders
        }
      }),
      { priority }
    );
  }
  
  /**
   * Upload a file
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data with files
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise} Promise resolving to response data
   */
  async uploadFile(endpoint, formData, onProgress = null) {
    const hotelHeaders = this.getHotelContextHeaders();
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...hotelHeaders
      }
    };
    
    if (onProgress) {
      config.onUploadProgress = progressEvent => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      };
    }
    
    return await this.api.post(endpoint, formData, config);
  }

  /**
   * Get current authenticated user
   * @returns {Promise} Promise resolving to user data
   */
  async getCurrentUser() {
    if (!this.isAuthenticated()) {
      return null;
    }
    // Several boot-time callers (app shell, SmartRedirect, demo card) ask
    // for the current user within the same tick; share one request.
    if (this._currentUserPromise) {
      return this._currentUserPromise;
    }
    this._currentUserPromise = (async () => {
      try {
        const user = await this.get('/v1/users/current');
        if (user) {
          // Validate user has hotel assignment (handles 4onboard exception)
          if (!validateUserHasHotel(user, () => this.logout())) {
            return null;
          }

          this.setCurrentUser(user);
        }
        return user;
      } catch (error) {
        console.error('Error getting current user:', error);
        return null;
      } finally {
        this._currentUserPromise = null;
      }
    })();
    return this._currentUserPromise;
  }

  /**
   * Initialize user context after login
   * Fetch and store current user data with hotel context
   */
  async initializeUserContext() {
    if (this.isAuthenticated() && !this.currentUser) {
      await this.getCurrentUser();
    }
  }

  /**
   * Determine request priority based on endpoint and method
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @returns {string} Priority level (high, normal, low)
   */
  _getRequestPriority(endpoint, method) {
    // Authentication endpoints should bypass queue entirely
    const bypassQueueEndpoints = ['/auth/token', '/auth/refresh'];
    if (bypassQueueEndpoints.some(path => endpoint.includes(path))) {
      return 'bypass'; // Special priority to indicate queue bypass
    }

    // High priority endpoints (critical user operations)
    const highPriorityEndpoints = [
      '/users/current',
      '/hotels/current/dashboard-stats',
      '/v1/reservations'
    ];
    
    // Low priority endpoints (background data)
    const lowPriorityEndpoints = [
      '/rates/seasons',
      '/rates/packages',
      '/reports/',
      '/v1/reports/'
    ];
    
    // Check for high priority
    if (highPriorityEndpoints.some(path => endpoint.includes(path))) {
      return 'high';
    }
    
    // Write operations get higher priority than reads
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      return 'normal';
    }
    
    // Check for low priority
    if (lowPriorityEndpoints.some(path => endpoint.includes(path))) {
      return 'low';
    }
    
    // Default priority for GET requests
    return 'normal';
  }

  /**
   * Get request queue statistics (for debugging)
   */
  getQueueStats() {
    return requestQueueManager.getStats();
  }

  /**
   * Request a password reset link (unauthenticated)
   */
  async requestPasswordReset(emailOrUsername, captchaToken) {
    const payload = { email_or_username: emailOrUsername };
    if (captchaToken) payload.cf_turnstile_token = captchaToken;
    return await axios.post(`${this.baseURL}/auth/password-reset/`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  /**
   * Confirm a password reset with uid and token (unauthenticated)
   */
  async confirmPasswordReset(uid, token, newPassword, confirmPassword, captchaToken) {
    const payload = { uid, token, new_password: newPassword, confirm_password: confirmPassword };
    if (captchaToken) payload.cf_turnstile_token = captchaToken;
    return await axios.post(`${this.baseURL}/auth/password-reset/confirm/`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  /**
   * Get current access token
   * @returns {string|null} Current access token
   */
  getToken() {
    return this.token;
  }

  /**
   * Get current user's hotel ID
   * @returns {number|null} Hotel ID from current user
   */
  getHotelId() {
    return this.currentUser?.hotel_id || this.currentUser?.hotel?.id || null;
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient();
export default apiClient;